<!--
  ONE COLLECTION — `/collection/heists`. A curated theme, its one-line pitch,
  and every member the catalogue can serve, as the same poster grid Browse
  draws for a genre.

  MEMBERSHIP IS DECIDED AT BUILD TIME, DRAWN AT RUN TIME. The committed list
  (see `lib/ui/collections.ts`) holds IMDb ids that were verified against
  Cinemeta when it was built, so this screen expects to find them all — but it
  still drops a title the catalogue has forgotten since, the same way
  AwardRail does, because a dead tile is worse than a shorter grid.

  FOUR REQUESTS IN FLIGHT, NOT TWENTY. Each member is one `meta` call. A page
  of twenty fired at once is rude to a public endpoint (AwardRail's argument);
  strictly sequential is twenty round trips a person watches one by one. Four
  workers is the polite middle, and the grid keeps the CURATED order however
  the answers race back.
-->
<script lang="ts">
  import type { CatalogItem } from '$types';
  import Empty from '$lib/ui/Empty.svelte';
  import Poster from '$lib/ui/Poster.svelte';
  import TileGrid from '$lib/ui/TileGrid.svelte';
  import Skeleton from '$lib/ui/Skeleton.svelte';
  import { collectionBySlug } from '$lib/ui/collections';
  import { detail } from '$lib/ui/cinemeta';
  import { nav } from '$lib/ui/nav.svelte';

  const slug = $derived(nav.route.name === 'collection' ? nav.route.slug : '');
  const collection = $derived(collectionBySlug(slug));

  let items = $state<CatalogItem[]>([]);
  let done = $state(false);

  $effect(() => {
    const c = collection;
    items = [];
    done = false;
    if (!c) {
      done = true;
      return;
    }

    const abort = new AbortController();
    const found: (CatalogItem | null)[] = new Array<CatalogItem | null>(c.members.length).fill(null);
    let cursor = 0;

    const worker = async (): Promise<void> => {
      while (!abort.signal.aborted) {
        const i = cursor++;
        const member = c.members[i];
        if (!member) return;
        try {
          found[i] = await detail(member.kind, member.id, abort.signal);
        } catch {
          if (abort.signal.aborted) return;
          /* Verified at build time but gone now: drop it, keep the grid. */
        }
        if (!abort.signal.aborted) items = found.filter((x): x is CatalogItem => x !== null);
      }
    };

    void Promise.all([worker(), worker(), worker(), worker()]).then(() => {
      if (!abort.signal.aborted) done = true;
    });

    return () => abort.abort();
  });

  const ghosts = Array.from({ length: 10 }, (_, i) => i);
</script>

<svelte:head>
  <title>{collection ? `${collection.label} — Jovii` : 'Collection — Jovii'}</title>
</svelte:head>

{#if !collection}
  <div class="pad">
    <Empty
      icon="grid"
      title="No collection here"
      detail="This link does not name a collection this app has. The ones it does have are on the home screen."
    />
  </div>
{:else}
  <header class="head">
    <h1>{collection.label}</h1>
    <p class="blurb">{collection.blurb}</p>
  </header>

  <section class="grid-wrap" aria-label={collection.label}>
    {#if items.length === 0 && !done}
      <div class="grid">
        {#each ghosts as n (n)}
          <div class="cell">
            <Skeleton ratio="2 / 3" />
            <Skeleton width="85%" height="0.72rem" radius="5px" />
          </div>
        {/each}
      </div>
    {:else if items.length === 0}
      <Empty
        icon="alert"
        tone="bad"
        title="The catalogue is not answering"
        detail="Cinemeta could not be reached, so this collection cannot be drawn. Try again in a moment."
      />
    {:else}
      <!-- TileGrid: the rail's focus/strip/tap manners on a wrapping grid. -->
      <TileGrid>
        {#each items as item, i (item.id)}
          <Poster {item} eager={i < 6} />
        {/each}
      </TileGrid>
      {#if done}
        <p class="count">{items.length} titles, picked by hand and resolved from public data.</p>
      {/if}
    {/if}
  </section>
{/if}

<style>
  .pad {
    padding: var(--space-5) var(--page-pad);
  }

  /*
   * The head clears the floating chrome on its own: this screen has no hero
   * whose artwork would otherwise run under the top bar, so the page itself
   * pays the offset.
   */
  .head {
    padding: calc(var(--chrome-overlap) + var(--space-6)) var(--page-pad) var(--space-2);
  }
  h1 {
    font-size: var(--text-2xl);
    font-weight: var(--weight-bold);
    letter-spacing: var(--tracking-tight);
  }
  .blurb {
    margin-top: var(--space-1);
    max-width: 62ch;
    color: var(--text-dim);
    font-size: var(--text-sm);
  }

  .grid-wrap {
    padding: var(--space-4) max(var(--page-pad), var(--safe-r)) var(--space-6)
      max(var(--page-pad), var(--safe-l));
  }
  .grid {
    display: grid;
    grid-template-columns: repeat(auto-fill, minmax(var(--tile-min), 1fr));
    gap: 18px var(--gap);
  }
  .cell {
    display: grid;
    gap: 7px;
  }
  .count {
    margin-top: var(--space-5);
    text-align: center;
    color: var(--text-faint);
    font-size: var(--text-xs);
  }
</style>
